import { useDroppable } from '@dnd-kit/core';
import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import classNames from 'classnames';
import { type FC, type ReactNode, useId } from 'react';
import type { DragType, FilterDropItem, TrackerDropItem } from './dnd';
import { useDraggedItem } from './TrackerDndProvider';

export type { FilterDropItem, TrackerDropItem } from './dnd';

interface DropZoneProps {
  acceptedElementIds: Array<DragType>;
  withIsOver?: boolean;
  onDrop: (item: TrackerDropItem | FilterDropItem) => void;
  /** A function child draws the hover state itself instead of the overlay. */
  children?: ReactNode | ((isOver: boolean) => ReactNode);
}

export const TrackerDropZone: FC<DropZoneProps> = ({
  children,
  acceptedElementIds,
  withIsOver = false,
  onDrop,
}) => {
  // `onDrop` rides along in `data` so the one onDragEnd handler in TrackerDndProvider can reach
  // the zone that was actually dropped on. dnd-kit keeps `data` in a ref, so it stays current.
  const { isOver, setNodeRef } = useDroppable({
    id: useId(),
    data: { accept: acceptedElementIds, onDrop },
  });
  const dragged = useDraggedItem();

  // `useDroppable` reports isOver for any drag; react-dnd's `accept` has to be re-applied here.
  // The drag-start snapshot, not dnd-kit's `active.data`: that empties once the virtual table
  // unmounts the dragged row.
  const showIsOver = isOver && dragged != null && acceptedElementIds.includes(dragged.itemType);

  return (
    <DropZone ref={setNodeRef} className={classNames({ isOver: showIsOver })}>
      {typeof children === 'function' ? children(showIsOver) : children}
      {showIsOver && withIsOver && typeof children !== 'function' && <Overlay />}
    </DropZone>
  );
};

const DropZone = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const Overlay = styled.div`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  position: absolute;
  float: left;
  box-sizing: content-box;
  border: 3px solid ${RacemapColors.Gray};
  background-color: ${RacemapColors.LightGray}50;
  border-radius: 3px;
`;
